import { ReactNode, useRef, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { NoteField } from './NoteField';

interface SubItemRowWrapperProps {
  label: string;
  children: ReactNode;
  note: string | undefined;
  isEditingNote: boolean;
  onNoteChange: (note: string) => void;
  onStartEditingNote: () => void;
  onStopEditingNote: () => void;
  onDelete: () => void;
  readOnly?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
}

export function SubItemRowWrapper({
  label,
  children,
  note,
  isEditingNote,
  onNoteChange,
  onStartEditingNote,
  onStopEditingNote,
  onDelete,
  readOnly = false,
  isSelected = false,
  onClick,
}: SubItemRowWrapperProps) {
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSelected && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isSelected]);

  return (
    <div
      className="relative group [&_input:focus]:!ring-2 [&_input:focus]:!ring-inset [&_input:focus]:!ring-blue-500 [&_input:focus]:!outline-none [&_textarea:focus]:!ring-2 [&_textarea:focus]:!ring-inset [&_textarea:focus]:!ring-blue-500 [&_textarea:focus]:!outline-none [&_[role=combobox]:focus]:!ring-2 [&_[role=combobox]:focus]:!ring-blue-500 [&_[role=combobox]:focus]:!outline-none"
      onClick={onClick}
      ref={rowRef}
    >
      <div className={`rounded px-3 py-1.5 border-2 ${
        isSelected
          ? 'border-blue-500 bg-blue-50 has-[input:focus]:border-gray-200 has-[input:focus]:bg-white has-[textarea:focus]:border-gray-200 has-[textarea:focus]:bg-white has-[[role=combobox]:focus]:border-gray-200 has-[[role=combobox]:focus]:bg-white'
          : 'border-gray-200'
      }`}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 h-7">
            <span className="text-sm font-medium text-gray-600 shrink-0">
              {label}
            </span>
            {!readOnly && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Delete subitem"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
          {children}
        </div>
        <NoteField
          note={note}
          isEditing={isEditingNote}
          onNoteChange={onNoteChange}
          onStartEditing={onStartEditingNote}
          onStopEditing={onStopEditingNote}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
}
